import React from 'react';
import { Drawer, Portal } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { LuCircleHelp } from 'react-icons/lu';
import { Tooltip } from '@/components/ui/tooltip.tsx';
import DrawerCloseButton from '@/components/ui/drawer-close-button.tsx';
import UploadGuidanceContent, {
	UPLOAD_GUIDANCE_TITLE,
} from '@/components/on-boarding/steps/documents/UploadGuidanceContent';
import {
	COMPOSER_PORTAL_CLICK_BOUNDARY_PROPS,
	COMPOSER_TOOLTIP_CLOSE_DELAY_MS,
	COMPOSER_TOOLTIP_OPEN_DELAY_MS,
} from './composerConstants.ts';
import { useCurrentHouse } from '@/context/CurrentHouseContext.tsx';
import { useAuth } from '@/context/AuthContext.tsx';
import { useHouseIngestEmail } from '@/hooks/house/useHouseIngestEmail.ts';
import { useComposerUploadCapabilities } from './useComposerUploadCapabilities.ts';

interface ComposerUploadHelpProps {
	onOpenChange?: (isOpen: boolean) => void;
}

/**
 * "?" affordance in the Records tab that opens the same "What should I upload?"
 * guidance used in onboarding/the documents tab, in a side drawer.
 */
const ComposerUploadHelp: React.FC<ComposerUploadHelpProps> = (props) => {
	const { onOpenChange } = props;
	const { house } = useCurrentHouse();
	const { currentUser } = useAuth();
	const { canUseEmailUpload } = useComposerUploadCapabilities();
	const { email: uploadEmail } = useHouseIngestEmail(house.id, { enabled: canUseEmailUpload });

	return (
		<Drawer.Root placement="end" size="md" onOpenChange={(details) => onOpenChange?.(details.open)}>
			<Tooltip
				openDelay={COMPOSER_TOOLTIP_OPEN_DELAY_MS}
				closeDelay={COMPOSER_TOOLTIP_CLOSE_DELAY_MS}
				content={UPLOAD_GUIDANCE_TITLE}
				positioning={{ placement: 'top' }}
			>
				<Drawer.Trigger asChild>
					<IconButton
						type="button"
						aria-label={UPLOAD_GUIDANCE_TITLE}
						variant="ghost"
						size="sm"
						color="fg.muted"
						boxSize={8}
						minW={8}
						flexShrink={0}
					>
						<LuCircleHelp />
					</IconButton>
				</Drawer.Trigger>
			</Tooltip>

			<Portal>
				<Drawer.Backdrop {...COMPOSER_PORTAL_CLICK_BOUNDARY_PROPS} />
				<Drawer.Positioner {...COMPOSER_PORTAL_CLICK_BOUNDARY_PROPS}>
					<Drawer.Content {...COMPOSER_PORTAL_CLICK_BOUNDARY_PROPS}>
						<Drawer.Header>
							<Drawer.Title>{UPLOAD_GUIDANCE_TITLE}</Drawer.Title>
							<DrawerCloseButton />
						</Drawer.Header>
						<Drawer.Body>
							<UploadGuidanceContent
								uploadEmail={uploadEmail}
								senderEmail={currentUser?.email ?? undefined}
								showEmailUpload={canUseEmailUpload}
							/>
						</Drawer.Body>
					</Drawer.Content>
				</Drawer.Positioner>
			</Portal>
		</Drawer.Root>
	);
};

export default ComposerUploadHelp;
